// Custom PrimeNG preset for Bidr, based on Nora
// Overrides specific design tokens using definePreset.
import { definePreset } from '@primeng/themes'
import Nora from '@primeng/themes/nora'
// No dark theme
const BidrPreset = definePreset(Nora, {
    primitive: {
        borderRadius: {
            xs: '0.375rem',
        },
    },
    semantic: {
        primary: {
            50: '{indigo.50}',
            100: '{indigo.100}',
            200: '{indigo.200}',
            300: '{indigo.300}',
            400: '{indigo.400}',
            500: '{indigo.500}',
            600: '{indigo.600}',
            700: '{indigo.700}',
            800: '{indigo.800}',
            900: '{indigo.900}',
            950: '{indigo.950}',
        },
        focusRing: {
            color: 'transparent',
            offset: '0',
            width: '1px',
        },
        formField: {
            borderRadius: '0.375rem',
            focusRing: {
                width: '1px',
                style: 'solid',
                color: '#86b7fe',
                offset: '0',
                shadow: '0 0 0 .25rem rgba(13, 110, 253, .25)', //Matches Bootstrap's focus shadow
            },
        },
        colorScheme: {
            light: {
                
                surface: {
                    200: '#eaeaea', //focus input background
                    500: '#dee2e6', //input border
                },
                primary: {
                    color: '#3c4c54',
                    hoverColor: '#3c4c54cc',
                    activeColor: '#3c4c54cc ',
                },
                highlight: {
                    background: '#007ad9',
                    focusBackground: '#007ad9',
                },
                overlay: {
                    select: {
                        borderColor: '#c8c8c8',
                    },
                },
                formField: {
                    iconColor: '#848484',
                }
            },
        },
    },
    components: {
        breadcrumb: {
            root: {
                padding: '0.5rem',
                background: '#eff1f1',
            },
        },
        card: {
            root: {
                shadow: 'none',
            },
            title: {
                fontSize: '1.2rem',
                fontWeight: '600',
            },
            css: () => `
.p-card {
    font-family: var(--p-font-family);
    border: solid 1px #d9d9d9;
    margin-bottom: 20px;
}
`,
        },
        button: {
            css: () => `
:root {
    --p-button-text-secondary-color: #007ad9;
    --p-button-label-font-weight: 400;
 }
.p-button {
    font-family: var(--p-font-family);
}
.p-button-icon.pi {
    font-size: 0.8rem !important;
}
            `,
        },
        datepicker: {
            panel: {
                borderColor: '#dee2e6',
            },
            dropdown: {
                background: '#555',
                borderColor: '#555',
                color: '#fff',
            },
            header: {
                borderColor: '#d8dae2',
            },
            date: {
                width: '2.5rem',
                height: '2.5rem',
                borderRadius: '3px',
            },
            title: { fontWeight: '700' },
            month: { padding: '.429rem 0' },
            weekDay: {
                padding: '.429rem 0',
                fontWeight: '700',
                color: '#000',
            },
            buttonbar: {
                borderColor: '#d8dae2',
            },
            dayView: { margin: '0 .0.5rem' },
            monthView: { margin: '0 .0.5rem' },
            yearView: { margin: '0 .0.5rem' },
            timePicker: {
                borderColor: '#d8dae2',
            },
            //z-index needed to appear above header
            css: () => `
:root {
    --p-datepicker-dropdown-hover-background: #828282;
    --p-datepicker-dropdown-hover-border-color: #828282;
    --p-datepicker-dropdown-hover-color: #fff;
    --p-datepicker-dropdown-active-background: #828282;
    --p-datepicker-dropdown-active-border-color: #828282;
    --p-datepicker-dropdown-active-color: #fff;
}
.p-datepicker .p-datepicker-panel{
    z-index: 1030;
}
.p-datepicker-panel {
    font-family: var(--p-font-family);
}
.p-datepicker-weekday{
    display: flex;
    justify-content: center;
    align-items: center;
}
        `,
        },
        checkbox: {
            root: {
                focusBorderColor: '#3c4c54',
                hoverBorderColor: '#3c4c54',
                disabledBackground: '#fff',
            },
            css: () => `
p-checkbox ~ label {
    margin-left: .5rem;
}
            `,
        },
        toast: {
            css: () => `
:root {
    --p-toast-success-background: #b7d8b7;     
    --p-toast-success-border-color: #b7d8b7;
    --p-toast-success-color: #212121;
    --p-toast-success-detail-color: #212121;
    --p-toast-error-background: #f8b7bd;
    --p-toast-error-border-color: #f8b7bd;
    --p-toast-error-color: #212121;
    --p-toast-error-detail-color: #212121;
}
    `   ,
        },
        confirmdialog: {
            css: () => `
:root {
    --p-dialog-content-padding: 1rem;
    --p-overlay-modal-padding: 1rem;
    --p-dialog-title-font-size: 1rem;
    --p-dialog-title-font-weight: 700;
}
.p-confirm-dialog {
    font-family: var(--p-font-family);
    font-size: 1rem;
    font-weight: 400;
}
.p-dialog .p-dialog-header {
    border-bottom: 1px solid #c8c8c8;
    background: #f4f4f4;
    color: #333;
}
.p-dialog .p-dialog-close-button{
    width: 2rem;
    height: 2rem;
    border: 0 none;
}
.p-dialog .p-dialog-content {
    padding: 1rem;
}
.p-dialog .p-dialog-footer {
    border-top: 1px solid #c8c8c8;
    padding: 1rem;
}
.p-dialog .p-dialog-footer .p-button {
    padding: .429rem 1rem;
}
.p-dialog .p-dialog-footer .p-button .pi {
    font-weight: 400;
    font-size: 0.8rem;
    line-height: 1.2rem;
}
            `,
        },
    },
    css: `
/* Global styles for Bidr preset */
:root {
    --p-font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
}
label.is--disabled {
    opacity: .5;
}
    `,
})
export default BidrPreset
